import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, requiredEmail } from 'utils/validations'
import { UIButton } from 'components/ui/Button'
import { KeyboardEvent, useState } from 'react'
import { showSuccessToast } from 'utils/toast'
import { useSelector } from 'react-redux'
import { messagesSelectors } from 'store/messages'
import { exportChat } from 'api/chats'
import { settingsSelectors } from 'store/settings'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useModal } from 'providers/ModalProvider'

type ExportChatModalProps = {
  onSuccess?: () => void
}

export const ExportChatModal = ({ onSuccess }: ExportChatModalProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const chatId = useSelector(messagesSelectors.chatId)
  const currentUser = useSelector(settingsSelectors.userDetails)
  const { close } = useModal()

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik({
    initialValues: {
      email: currentUser?.email || '',
    },
    validationSchema: createValidationSchema({
      email: requiredEmail,
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      console.debug({ values })
      if (!chatId) {
        return
      }
      try {
        setLoading(true)
        await exportChat(chatId, values.email)
        onSuccess?.()
        showSuccessToast(t('chatExportedSuccess'))
        close()
      } catch (e) {
        console.error({ e })
      } finally {
        setLoading(false)
      }
    },
  })

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  return (
    <Modal onClose={close}>
      <ModalHeader title={t('exportChat')} icon="envelope" />
      <ModalBody className={'tw-space-y-1'}>
        <Input type={'email'} label={t('email')} {...fieldProps('email')} onKeyDown={onKeyDown} />
        <div className={'tw-h-2'} />
      </ModalBody>
      <ModalFooter>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={loading}>
          <span className={'tw-text-sm'}>{t('exportChat')}</span>
        </UIButton>
      </ModalFooter>
    </Modal>
  )
}
